import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ onQuoteClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isEquipmentActive = location.pathname.startsWith('/equipment');
  const isHomePage = location.pathname === '/';

  const headerBg = isHomePage && !isScrolled ? 'bg-transparent' : 'bg-[#1a2332]';

  // If already on the target route, scroll to top instead of no-op navigating
  const handleNavClick = (e, targetPath) => {
    if (location.pathname === targetPath) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navLinkClass = (active) =>
    `font-semibold transition-colors ${
      active ? 'text-[#E28724]' : 'text-white hover:text-[#E28724]'
    }`;

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${headerBg} border-gray-700 shadow-lg`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="/logo.png"
              alt="Cooper Equipment Rentals"
              className="h-10 sm:h-12 md:h-14 w-auto cursor-pointer"
              onClick={() => {
                if (location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  navigate('/');
                }
              }}
              data-testid="header-logo"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              onClick={(e) => handleNavClick(e, '/')}
              className={navLinkClass(isActive('/'))}
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link
              to="/equipment"
              onClick={(e) => handleNavClick(e, '/equipment')}
              className={navLinkClass(isEquipmentActive)}
              data-testid="nav-equipment"
            >
              Equipment
            </Link>
            <Link
              to="/about"
              onClick={(e) => handleNavClick(e, '/about')}
              className={navLinkClass(isActive('/about'))}
              data-testid="nav-about"
            >
              About Us
            </Link>
            <Link
              to="/contact"
              onClick={(e) => handleNavClick(e, '/contact')}
              className={navLinkClass(isActive('/contact'))}
              data-testid="nav-contact"
            >
              Contact
            </Link>
            <Link
              to="/faq"
              onClick={(e) => handleNavClick(e, '/faq')}
              className={navLinkClass(isActive('/faq'))}
              data-testid="nav-faq"
            >
              FAQ
            </Link>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:714-379-5545"
              className="flex items-center text-white hover:text-[#E28724] font-medium transition-colors text-sm lg:text-base"
              data-testid="header-phone-link"
            >
              <Phone className="w-4 h-4 mr-2" />
              <span className="hidden lg:inline">714-379-5545</span>
              <span className="lg:hidden">Call Now</span>
            </a>
            <Button
              onClick={onQuoteClick}
              className="bg-[#E28724] hover:bg-[#e67d0f] text-white px-4 lg:px-6 py-2 transition-colors text-sm lg:text-base font-semibold"
              data-testid="header-request-quote-button"
            >
              Request Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-md text-white hover:bg-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-700 bg-[#1a2332]" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-2">
              <Link
                to="/"
                className="text-white hover:text-[#E28724] font-semibold py-2"
                onClick={(e) => {
                  handleNavClick(e, '/');
                  setMobileMenuOpen(false);
                }}
                data-testid="mobile-nav-home"
              >
                Home
              </Link>
              <Link
                to="/equipment"
                className={`font-semibold py-2 ${isEquipmentActive ? 'text-[#E28724]' : 'text-white hover:text-[#E28724]'}`}
                onClick={(e) => {
                  handleNavClick(e, '/equipment');
                  setMobileMenuOpen(false);
                }}
                data-testid="mobile-nav-equipment"
              >
                Equipment
              </Link>
              <Link
                to="/about"
                className="text-white hover:text-[#E28724] font-semibold py-2"
                onClick={(e) => {
                  handleNavClick(e, '/about');
                  setMobileMenuOpen(false);
                }}
                data-testid="mobile-nav-about"
              >
                About Us
              </Link>
              <Link
                to="/contact"
                className="text-white hover:text-[#E28724] font-semibold py-2"
                onClick={(e) => {
                  handleNavClick(e, '/contact');
                  setMobileMenuOpen(false);
                }}
                data-testid="mobile-nav-contact"
              >
                Contact
              </Link>
              <Link
                to="/faq"
                className="text-white hover:text-[#E28724] font-semibold py-2"
                onClick={(e) => {
                  handleNavClick(e, '/faq');
                  setMobileMenuOpen(false);
                }}
                data-testid="mobile-nav-faq"
              >
                FAQ
              </Link>

              <div className="pt-4 border-t border-gray-700 flex flex-col space-y-3">
                <a
                  href="tel:714-379-5545"
                  className="flex items-center text-white font-medium"
                  data-testid="mobile-phone-link"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  714-379-5545
                </a>
                <Button
                  onClick={() => {
                    onQuoteClick();
                    setMobileMenuOpen(false);
                  }}
                  className="bg-[#E28724] hover:bg-[#e67d0f] text-white w-full"
                  data-testid="mobile-request-quote-button"
                >
                  Request Quote
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
